// Página PÚBLICA de gestión de una reserva (Pilar 1). El lead llega con el
// link del email de confirmación. Permite ver, cancelar o elegir otro horario
// (v1 = cancelar + redirigir a la página de reserva).

import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Calendar, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { usePublicAgendaTheme } from '~/lib/public-agenda-theme';
import styles from './agenda.$slug.module.css';

const PB_URL = import.meta.env.VITE_PB_URL || 'https://api.lifefocus.agency';

export const Route = createFileRoute('/agenda/gestionar/$token')({ component: ManagePage });

interface BookingInfo {
  status: string;
  start: string;
  when_local: string;
  timezone: string;
  page_name: string;
  booker_name: string;
  meeting_link?: string;
  tenant_slug?: string;
  page_slug?: string;
  book_url?: string;
}

function ManagePage() {
  const { token } = useParams({ from: '/agenda/gestionar/$token' });
  usePublicAgendaTheme();
  const [info, setInfo] = useState<BookingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState<'cancel' | 'reschedule' | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`${PB_URL}/api/booking/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: BookingInfo) => {
        if (!alive) return;
        setInfo(d);
        setCancelled(d.status === 'cancelled');
      })
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  function bookHref(): string | null {
    if (info?.book_url) return info.book_url;
    if (info?.tenant_slug && info?.page_slug) {
      return `/agenda/${info.tenant_slug}/${info.page_slug}`;
    }
    return null;
  }

  async function cancelRemote(): Promise<boolean> {
    const res = await fetch(`${PB_URL}/api/booking/${encodeURIComponent(token)}/cancel`, {
      method: 'POST',
    });
    return res.ok;
  }

  async function handleCancel() {
    setBusy('cancel');
    setActionErr(null);
    try {
      if (await cancelRemote()) setCancelled(true);
      else setActionErr('No se pudo cancelar. Intenta de nuevo o escríbenos.');
    } catch {
      setActionErr('No se pudo cancelar. Revisa tu conexión e intenta de nuevo.');
    } finally {
      setBusy(null);
    }
  }

  async function handleReschedule() {
    const href = bookHref();
    if (!href) {
      setActionErr('No encontramos la página de reserva. Cancela y agenda de nuevo desde el sitio.');
      return;
    }
    setBusy('reschedule');
    setActionErr(null);
    try {
      if (!(await cancelRemote())) {
        setActionErr('No se pudo liberar el horario. Intenta de nuevo.');
        return;
      }
      window.location.assign(href);
    } catch {
      setActionErr('No se pudo cambiar el horario. Revisa tu conexión e intenta de nuevo.');
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={styles.card}>
          <Loader2 className={styles.spin} size={28} />
        </div>
      </div>
    );
  }
  if (notFound || !info) {
    return (
      <div className={styles.shell}>
        <div className={styles.card}>
          <p className={styles.errorText}>No encontramos esta reserva.</p>
        </div>
      </div>
    );
  }

  const href = bookHref();

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        {cancelled ? (
          <>
            <div className={styles.confirmIcon} style={{ color: 'var(--color-danger-strong)' }}>
              <XCircle size={40} />
            </div>
            <h1 className={styles.confirmTitle}>Reunión cancelada</h1>
            <p className={styles.confirmText}>
              Tu reunión fue cancelada.
              {href ? ' Si quieres, elige otro horario.' : ' Si fue un error, agenda una nueva desde el sitio.'}
            </p>
            {href && (
              <a href={href} className={styles.confirmBtn} style={{ textDecoration: 'none', textAlign: 'center' }}>
                Elegir otro horario
              </a>
            )}
          </>
        ) : (
          <>
            <header className={styles.header}>
              <Calendar size={20} aria-hidden />
              <div>
                <h1 className={styles.title}>{info.page_name || 'Tu reunión'}</h1>
                <p className={styles.subtitle}>{info.booker_name}</p>
              </div>
            </header>
            <div className={styles.bookForm}>
              <p className={styles.bookFormTitle} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={16} color="var(--color-success)" /> Confirmada para {info.when_local} (
                {info.timezone.replace('_', ' ')})
              </p>
              <div className={styles.manageActions}>
                {info.meeting_link && (
                  <a
                    href={info.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.confirmBtn}
                    style={{ textDecoration: 'none', textAlign: 'center' }}
                  >
                    Unirme a la videollamada
                  </a>
                )}
                {href && (
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => void handleReschedule()}
                    disabled={!!busy}
                  >
                    {busy === 'reschedule' ? (
                      <>
                        <Loader2 size={14} className={styles.spin} /> Liberando horario…
                      </>
                    ) : (
                      'Elegir otro horario'
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className={`${styles.confirmBtn} ${styles.btnDanger}`}
                  onClick={() => void handleCancel()}
                  disabled={!!busy}
                >
                  {busy === 'cancel' ? (
                    <>
                      <Loader2 size={14} className={styles.spin} /> Cancelando…
                    </>
                  ) : (
                    'Cancelar reunión'
                  )}
                </button>
              </div>
              {actionErr && <p className={styles.errorText}>{actionErr}</p>}
            </div>
          </>
        )}
      </div>
      <p className={styles.brand}>
        Agendado con{' '}
        <a
          href="https://lifefocus.agency/sistema-focus"
          target="_blank"
          rel="noopener"
          className={styles.brandLink}
        >
          Sistema Focus
        </a>
      </p>
    </div>
  );
}
